import { Node, Edge } from '@xyflow/react';
import { Json } from '@/integrations/supabase/types';

// Type definitions for better type safety
export interface NodeData {
  label: {
    props: {
      children: Array<{
        props?: {
          children: string;
        };
      }>;
    };
  };
  kpiType?: string;
  title?: string;
  description?: string;
  fullDescription?: string;
}

export interface KpiData {
  exec_kpis: Json;
  business_initiatives: Json;
  core_financial_kpis: Json;
  operating_kpis: Json;
}

export const isNodeData = (data: any): data is NodeData => {
  return data && 
    typeof data === 'object' && 
    'label' in data && 
    typeof data.label === 'object' && 
    data.label !== null && 
    'props' in data.label && 
    Array.isArray(data.label.props?.children);
};

export const validateAndConvertEdges = (edges: any): Edge[] => {
  if (!Array.isArray(edges)) return [];
  return edges.filter((edge): edge is Edge => {
    return typeof edge === 'object' && 
      edge !== null && 
      typeof edge.id === 'string' && 
      typeof edge.source === 'string' && 
      typeof edge.target === 'string';
  });
};

export const calculateRectangleWidth = (nodes: Node[], yPosition: number) => {
  const nodesInArea = nodes.filter(node => {
    if (!node.position) return false;
    return Math.abs(node.position.y - yPosition) < 50;
  });
  
  const minWidth = 1700;
  const nodeWidth = 270;
  const padding = 0;
  
  //return Math.max(minWidth, nodesInArea.length * nodeWidth + padding);
  return Math.max(minWidth, 0);
};

export const parseKPIContent = (content: string | Json): Array<{title: string, description: string}> => {
  if (!content) return [];
  
  // If content is not a string, convert it to a string
  const contentStr = typeof content === 'string' ? content : JSON.stringify(content);
  
  // Split by bullet points
  const bulletItems = contentStr.split('•').filter(item => item.trim().length > 0);
  const result: Array<{title: string, description: string}> = [];
  
  for (const item of bulletItems) {
    const trimmedItem = item.trim();
    // Find the position of the first colon
    const colonIndex = trimmedItem.indexOf(':');
    
    if (colonIndex !== -1) {
      const title = trimmedItem.substring(0, colonIndex).trim().replace(/\*\*/g, '');
      let description = trimmedItem.substring(colonIndex + 1).trim();
      
      // Clean up the description (remove ** and normalize whitespace)
      description = description.replace(/^\*\*/, '').trim();
      
      if (title && description) {
        result.push({ title, description });
      }
    } else {
      // For items without a colon, try to extract meaningful title and description
      const parts = trimmedItem.split('**');
      if (parts.length >= 2) {
        const title = parts[0].trim();
        const description = parts.slice(1).join('').trim();
        
        if (title && description) {
          result.push({ title, description });
        }
      }
    }
  }
  
  return result;
};

export const getNodePositionsForKPIType = (
  kpiType: string,
  existingNodes: Node[],
  totalNodes: number,
  index: number
): { x: number, y: number } => {

  // ** define a height gap of 120px between different KPI category nodes
  const areaPositions = {
    'executive': { y: 15 },
    'financial': { y: 135 },
    'initiatives': { y: 255 },
    'operating': { y: 375 },
    'solutions': { y: 495 }
  };
  
  const baseY = areaPositions[kpiType as keyof typeof areaPositions]?.y || 0;
  
  // Calculate horizontal position based on total nodes and index
  // Reserve 15% for left margin, use 85% for nodes

  // ** startX is the starting X position for the first node in each KPI category
  const startX = 250; // Starting after the label
  const endX = 1700 - 20; // Full width minus right margin
  const availableWidth = endX - startX;
  
  // If there's only one node, center it
  if (totalNodes === 1) {
    return { x: startX + availableWidth / 2, y: baseY + 5 };
  }
  
  // Otherwise, distribute nodes evenly
  const nodeWidth = Math.min(250, (availableWidth * 0.85) / totalNodes);
  const spacing = (availableWidth * 0.85 - nodeWidth * totalNodes) / (totalNodes - 1);
  const x = startX + (nodeWidth + spacing) * index;
  
  return { x, y: baseY + 5 };
};

export const initialNodes: Node[] = [
  // Executive area
  {
    id: 'executive-area',
    type: 'group',
    position: { x: 0, y: 0 },
    style: {
      width: 1400,
      height: 125,
      backgroundColor: '#9b87f5',
      borderRadius: '8px',
      padding: '10px'
    },
    data: { label: '' }
  },
  {
    id: 'executive-label',
    type: 'label',
    position: { x: 20, y: 10 },
    data: { label: 'Executive KPIs' },
    draggable: false,
    connectable: false,
    style: {
      background: 'transparent',
      border: 'none',
      fontSize: '1.25rem',
      fontWeight: 500,
      color: '#000000',
      width: 'auto',
      boxShadow: 'none'
    }
  },
  
  // Financial area
  {
    id: 'financial-area',
    type: 'group',
    position: { x: 0, y: 120 },
    style: {
      width: 1400,
      height: 125,
      backgroundColor: '#90CAF9',
      borderRadius: '8px',
      padding: '10px'
    },
    data: { label: '' }
  },
  {
    id: 'financial-label',
    type: 'label',
    position: { x: 20, y: 130 },
    data: { label: 'Core Financial KPIs' },
    draggable: false,
    connectable: false,
    style: {
      background: 'transparent',
      border: 'none',
      fontSize: '1.25rem',
      fontWeight: 500,
      color: '#000000',
      width: 'auto',
      boxShadow: 'none'
    }
  },
  
  // Initiatives area
  {
    id: 'initiatives-area',
    type: 'group',
    position: { x: 0, y: 240 },
    style: {
      width: 1400,
      height: 125,
      backgroundColor: '#81C784',
      borderRadius: '8px',
      padding: '10px'
    },
    data: { label: '' }
  },
  {
    id: 'initiatives-label',
    type: 'label',
    position: { x: 20, y: 250 },
    data: { label: 'Business Initiatives' },
    draggable: false,
    connectable: false,
    style: {
      background: 'transparent',
      border: 'none',
      fontSize: '1.25rem',
      fontWeight: 500,
      color: '#000000',
      width: 'auto',
      boxShadow: 'none'
    }
  },
  
  // Operating area
  {
    id: 'operating-area',
    type: 'group',
    position: { x: 0, y: 360 },
    style: {
      width: 1400,
      height: 125,
      backgroundColor: '#FFA726',
      borderRadius: '8px',
      padding: '10px'
    },
    data: { label: '' }
  },
  {
    id: 'operating-label',
    type: 'label',
    position: { x: 20, y: 370 },
    data: { label: 'Operating KPIs' },
    draggable: false,
    connectable: false,
    style: {
      background: 'transparent',
      border: 'none',
      fontSize: '1.25rem',
      fontWeight: 500,
      color: '#000000',
      width: 'auto',
      boxShadow: 'none'
    }
  },
  
  // Solutions area
  {
    id: 'solutions-area',
    type: 'group',
    position: { x: 0, y: 480 },
    style: {
      width: 1400,
      height: 125,
      backgroundColor: '#8A898C',
      borderRadius: '8px',
      padding: '10px'
    },
    data: { label: '' }
  },
  {
    id: 'solutions-label',
    type: 'label',
    position: { x: 20, y: 490 },
    data: { label: 'Potential Solutions' },
    draggable: false,
    connectable: false,
    style: {
      background: 'transparent',
      border: 'none',
      fontSize: '1.25rem',
      fontWeight: 500,
      color: '#000000',
      width: 'auto',
      boxShadow: 'none'
    }
  }
];
